import React from "react";
import { Paper, Typography, Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";

import { getUserDateFormat, getUserTimeFormat } from "../../utils/formatDate";

export default function RouteTripTime({ tripData, setTripData, isUpdateMode }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const [errors, setErrors] = React.useState({
    tripDate: false,
    startTime: false,
    endTime: false,
    startTimeMsg: "",
    endTimeMsg: "",
  });

  // Get user preferences
  const userDateFormat = getUserDateFormat();
  const userTimeFormat = getUserTimeFormat();

  // Convert date format for dayjs
  const getDayjsFormat = (format) => {
    const formatMap = {
      "DD/MM/YYYY": "DD/MM/YYYY",
      "MM/DD/YYYY": "MM/DD/YYYY",
      "YYYY/MM/DD": "YYYY/MM/DD",
      "DD-MM-YYYY": "DD-MM-YYYY",
      "MM-DD-YYYY": "MM-DD-YYYY",
      "YYYY-MM-DD": "YYYY-MM-DD",
      "DD.MM.YYYY": "DD.MM.YYYY",
      "MM.DD.YYYY": "MM.DD.YYYY",
      "YYYY.MM.DD": "YYYY.MM.DD",
    };
    return formatMap[format] || "DD/MM/YYYY";
  };

  // Get time format for dayjs
  const getTimeFormat = () => {
    return userTimeFormat === "24hr" ? "HH:mm" : "hh:mm A";
  };

  // Get time placeholder based on format
  const getTimePlaceholder = () => {
    return userTimeFormat === "24hr" ? "-- : --" : "-- : -- --";
  };

  const isSameOrBeforeMinute = (candidateTime, referenceTime) => {
    if (!candidateTime || !referenceTime) {
      return false;
    }

    return (
      candidateTime.isBefore(referenceTime, "minute") ||
      candidateTime.isSame(referenceTime, "minute")
    );
  };

  const handleInputChange = (field, value) => {
    // Validate start time if it's today
    if (field === "startTime" && value && tripData.tripDate) {
      if (tripData.tripDate.isSame(dayjs(), "day")) {
        // Don't allow times in the past for today
        if (value.isBefore(dayjs(), "minute")) {
          setErrors((prev) => ({
            ...prev,
            startTime: true,
            startTimeMsg: "Start time cannot be in the past",
          }));
          return; // Reject the change
        }
      }
      setErrors((prev) => ({
        ...prev,
        startTime: false,
        startTimeMsg: "",
      }));
    }

    // Validate end time
    if (field === "endTime" && value) {
      let errorMsg = "";
      
      // End time cannot be same as or before start time
      if (tripData.startTime && (value.isBefore(tripData.startTime, "minute") || value.isSame(tripData.startTime, "minute"))) {
        errorMsg = "End time must be after start time";
        setErrors((prev) => ({
          ...prev,
          endTime: true,
          endTimeMsg: errorMsg,
        }));
        return; // Reject the change
      }
      
      // If trip date is today, don't allow past times
      if (tripData.tripDate && tripData.tripDate.isSame(dayjs(), "day")) {
        if (value.isBefore(dayjs(), "minute")) {
          errorMsg = "End time cannot be in the past";
          setErrors((prev) => ({
            ...prev,
            endTime: true,
            endTimeMsg: errorMsg,
          }));
          return; // Reject the change
        }
      }
      
      setErrors((prev) => ({
        ...prev,
        endTime: false,
        endTimeMsg: "",
      }));
    }

    // Validate trip date
    if (field === "tripDate" && value) {
      // Don't allow past dates
      if (value.isBefore(dayjs(), "day")) {
        return; // Reject the change
      }
      // If changing date, clear times to force re-entry
      if (!value.isSame(tripData.tripDate, "day")) {
        setTripData((prev) => ({
          ...prev,
          [field]: value,
          startTime: null,
          endTime: null,
        }));
        setErrors((prev) => ({
          ...prev,
          startTime: false,
          startTimeMsg: "",
          endTime: false,
          endTimeMsg: "",
        }));
        return;
      }
    }

    setTripData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: false,
      }));
    }
  };

  const textFieldStyles = (hasError) => ({
    "& .MuiOutlinedInput-root": {
      borderRadius: "16px",
      height: "43px",
      backgroundColor: "#FFFFFF",
      "& fieldset": {
        borderColor: hasError ? "#d32f2f" : "#95A0B3",
        borderWidth: "2px",
      },
      "&:hover fieldset": {
        borderColor: hasError ? "#d32f2f" : "#20A8E0",
      },
      "&.Mui-focused fieldset": {
        borderColor: hasError ? "#d32f2f" : "#20A8E0",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "8px 14px",
      fontSize: "14px",
      fontFamily: "Roboto",
    },
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1, sm: 1 },
          width: "100%",
          borderRadius: "8px",
          display: "flex",
          flexDirection: isSmall ? "column" : "row",
          alignItems: isSmall ? "flex-start" : "center",
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: isSmall ? "column" : "row",
            gap: 2,
            width: "100%",
            alignItems: isSmall ? "flex-start" : "center",
          }}
        >
          <Box sx={{ flex: 1, width: "100%" }}>
            <Typography
              sx={{
                fontSize: "16px",
                fontFamily: "Roboto",
                fontWeight: 500,
                color: "#183059",
                mb: 1,
              }}
            >
              {t("Trip date")}
            </Typography>
            <DatePicker
              value={tripData.tripDate}
              onChange={(newValue) => handleInputChange("tripDate", newValue)}
              minDate={
                !isUpdateMode || !tripData.tripDate ? dayjs() : undefined
              }
              shouldDisableDate={(date) => {
                if (isUpdateMode && tripData.tripDate) {
                  // In update mode with existing date, only allow future dates or same date
                  return date.isBefore(dayjs(), "day");
                }
                return false;
              }}
              format={getDayjsFormat(userDateFormat)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: errors.tripDate,
                  sx: textFieldStyles(errors.tripDate),
                  placeholder: getDayjsFormat(userDateFormat),
                },
              }}
            />
            {errors.tripDate && (
              <Typography
                color="error"
                variant="caption"
                sx={{ pl: 1, display: "block", mt: 0.5 }}
              >
                {t("Trip date is required")}
              </Typography>
            )}
          </Box>
          <Box sx={{ flex: 1, width: "100%" }}>
            <Typography
              sx={{
                fontSize: "16px",
                fontFamily: "Roboto",
                fontWeight: 500,
                color: "#183059",
                mb: 1,
              }}
            >
              {t("Start time")}
            </Typography>
            <TimePicker
              value={tripData.startTime}
              onChange={(newValue) => handleInputChange("startTime", newValue)}
              format={getTimeFormat()}
              ampm={userTimeFormat === "12hr"}
              shouldDisableTime={(value, view) => {
                // If trip date is today, disable times in the past
                if (tripData.tripDate && tripData.tripDate.isSame(dayjs(), "day")) {
                  if (view === "hours") {
                    return value.isBefore(dayjs(), "hour");
                  }
                  if (view === "minutes") {
                    const hour = value.hour();
                    const currentHour = dayjs().hour();
                    if (hour === currentHour) {
                      return value.isBefore(dayjs(), "minute");
                    }
                    return hour < currentHour;
                  }
                }
                return false;
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: errors.startTime,
                  sx: textFieldStyles(errors.startTime),
                  placeholder: getTimePlaceholder(),
                },
                openPickerButton: {
                  sx: { pointerEvents: "none" },
                },
                popper: {
                  sx: { display: "none" },
                },
              }}
            />
            {errors.startTime && (
              <Typography
                color="error"
                variant="caption"
                sx={{ pl: 1, display: "block", mt: 0.5 }}
              >
                {errors.startTimeMsg || t("Start time is required")}
              </Typography>
            )}
          </Box>
          <Box sx={{ flex: 1, width: "100%" }}>
            <Typography
              sx={{
                fontSize: "16px",
                fontFamily: "Roboto",
                fontWeight: 500,
                color: "#183059",
                mb: 1,
              }}
            >
              {t("End time")}
            </Typography>
            <TimePicker
              value={tripData.endTime}
              onChange={(newValue) => handleInputChange("endTime", newValue)}
              format={getTimeFormat()}
              ampm={userTimeFormat === "12hr"}
              shouldDisableTime={(value, view) => {
                // If trip date is today, disable times in the past
                if (tripData.tripDate && tripData.tripDate.isSame(dayjs(), "day")) {
                  if (view === "hours") {
                    return value.isBefore(dayjs(), "hour");
                  }
                  if (view === "minutes") {
                    const hour = value.hour();
                    const currentHour = dayjs().hour();
                    if (hour === currentHour) {
                      return value.isBefore(dayjs(), "minute");
                    }
                    return hour < currentHour;
                  }
                }
                
                // End time cannot be same as or before start time
                if (tripData.startTime) {
                  if (view === "hours") {
                    return value.hour() < tripData.startTime.hour();
                  }
                  if (view === "minutes") {
                    return (
                      value.hour() === tripData.startTime.hour() &&
                      isSameOrBeforeMinute(value, tripData.startTime)
                    );
                  }
                }
                
                return false;
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: errors.endTime,
                  sx: textFieldStyles(errors.endTime),
                  placeholder: getTimePlaceholder(),
                },
                openPickerButton: {
                  sx: { pointerEvents: "none" },
                },
                popper: {
                  sx: { display: "none" },
                },
              }}
            />
            {errors.endTime && (
              <Typography
                color="error"
                variant="caption"
                sx={{ pl: 1, display: "block", mt: 0.5 }}
              >
                {errors.endTimeMsg || t("End time is required")}
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
    </LocalizationProvider>
  );
}
